// MIDAS V3 — Feedback panel (Phase B — R5 sub-task 5b)
//
// Slide-down comment panel that appears below the message action row after
// the user clicks Like or Dislike. On Like: just a comment input. On Dislike:
// comment input + "Was the source wrong?" Yes/No/Skip toggle.
//
// Strategy: scratch (no v2 equivalent — Phase 5 is the first UX layer on top
//   of the bot's feedback endpoint).
// Verified no-MUI: yes — plain <div>, <textarea>, <button> with inline styles
//   and v3 CSS custom properties.
// Phase: R5 5b.
//
// Per MIDAS_V3_BUGS_0519_R5_SPEC.md sub-task 5b:
//   • Comment input — Sora 13, var(--card-2) bg, var(--border) border. Soft
//     limit 2000 chars. Counter visible at 1900+.
//   • Dislike adds 3-pill Yes/No/Skip for source-wrong. Default Skip → null.
//   • Submit on click of send button OR Enter (Shift+Enter = newline).
//   • Animation: CSS height transition + opacity, reduced-motion friendly.
//
// Note on POST semantics (updated 2026-05-23):
//   The panel owns the SINGLE POST. MessageActionsV3 no longer POSTs on the
//   Like/Dislike click — it only opens this panel + toggles the local v2 icon.
//   When the user submits here (Send button or Enter), we dispatch ONE
//   submitFeedback with action + optional comment + (for dislike) optional
//   source_wrong. This avoids the 409 duplicate_feedback the bot returned when
//   click-time POST + panel-submit-POST both fired with the same (message_id,
//   action) key.
//
//   If the user closes the panel without typing, no POST happens — accepted
//   trade-off: an empty "I clicked like" carries little signal anyway. The
//   local v2 toggle still highlights the icon.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Send, X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../../../shared/hooks/sharedHooks";
import {
  submitFeedback,
  FEEDBACK_REJECT_MESSAGE_NOT_FOUND,
} from "./midasMessageFeedbackV3Slice";
import useTranslation from "../../../../../shared/hooks/useTranslation";

const MAX_COMMENT_LENGTH = 2000;
const COMMENT_COUNTER_THRESHOLD = 1900;

export interface FeedbackPanelV3Props {
  /** Bot-minted message id (ChatMessage.botMessageId). Required. */
  botMessageId: string;
  /** Active thread id from midasPageSlice. Optional. */
  threadId?: string | null;
  /** Which action the user just clicked. Drives placeholder text + whether the
   *  source-wrong toggle is shown. */
  action: "like" | "dislike";
  /** Called when the user closes the panel via the ✕ button or after submit. */
  onClose: () => void;
  /** Called when the bot replies 404 message_not_found. Parent should revert
   *  the local v2 toggle so the icon doesn't stay highlighted for a row that
   *  never persisted on the bot side. */
  onMessageNotFound?: (action: "like" | "dislike") => void;
}

type SourceWrongChoice = "yes" | "no" | "skip";

const FeedbackPanelV3: React.FC<FeedbackPanelV3Props> = ({
  botMessageId,
  threadId,
  action,
  onClose,
  onMessageNotFound,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [comment, setComment] = useState("");
  const [sourceWrong, setSourceWrong] = useState<SourceWrongChoice>("skip");
  const [submitting, setSubmitting] = useState(false);
  const [recorded, setRecorded] = useState(false);

  const feedback = useAppSelector(
    (s) => s.midasMessageFeedbackV3.byMessageId[botMessageId]
  );

  // Focus the textarea on mount for keyboard-first UX.
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleCommentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const next = e.target.value;
      // Soft-truncate at the cap — drop excess characters.
      setComment(next.length > MAX_COMMENT_LENGTH
        ? next.slice(0, MAX_COMMENT_LENGTH)
        : next);
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    const sourceWrongValue =
      action === "dislike" && sourceWrong !== "skip"
        ? sourceWrong === "yes"
        : null;
    try {
      const result = await dispatch(
        submitFeedback({
          messageId: botMessageId,
          threadId,
          action,
          comment: comment.trim() || undefined,
          sourceWrong: sourceWrongValue,
        })
      );
      // 404 message_not_found → ask the parent to revert the local toggle and
      // close the panel without "Recorded". The slice already logged the warning.
      if (
        submitFeedback.rejected.match(result) &&
        result.payload === FEEDBACK_REJECT_MESSAGE_NOT_FOUND
      ) {
        onMessageNotFound?.(action);
        onClose();
        return;
      }
      // Fulfilled (including soft-success 409 duplicate_feedback) → show
      // "Recorded" then close. Any other reject (400/401/403/429 etc.) leaves
      // the panel open with the footer error so the user can retry.
      if (submitFeedback.fulfilled.match(result)) {
        setRecorded(true);
        window.setTimeout(() => {
          onClose();
        }, 1500);
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting,
    dispatch,
    botMessageId,
    threadId,
    action,
    comment,
    sourceWrong,
    onClose,
    onMessageNotFound,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter (no Shift) submits. Shift+Enter → newline (default behavior).
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  // ─── Strings ────────────────────────────────────────────────────────
  const labels = {
    likePlaceholder: translate("midas.v3.feedback.likeCommentPlaceholder", {
      defaultValue: "What did you like? (optional)",
    }) as string,
    dislikePlaceholder: translate("midas.v3.feedback.dislikeCommentPlaceholder", {
      defaultValue: "What was wrong? (optional)",
    }) as string,
    sourceWrongQuestion: translate("midas.v3.feedback.sourceWrongQuestion", {
      defaultValue: "Was the source wrong?",
    }) as string,
    yes: translate("midas.v3.feedback.sourceWrong.yes", {
      defaultValue: "Yes",
    }) as string,
    no: translate("midas.v3.feedback.sourceWrong.no", {
      defaultValue: "No",
    }) as string,
    skip: translate("midas.v3.feedback.sourceWrong.skip", {
      defaultValue: "Skip",
    }) as string,
    submit: translate("midas.v3.feedback.submit", {
      defaultValue: "Send",
    }) as string,
    charCount: translate("midas.v3.feedback.charCount", {
      defaultValue: "{{n}} / 2000",
      n: comment.length,
    }) as string,
    recorded: translate("midas.v3.feedback.recorded", {
      defaultValue: "Recorded",
    }) as string,
    close: translate("midas.v3.feedback.close", {
      defaultValue: "Close",
    }) as string,
    errorRetry: translate("midas.v3.feedback.errorRetry", {
      defaultValue: "Couldn't save feedback — try again",
    }) as string,
  };

  const placeholder = action === "like" ? labels.likePlaceholder : labels.dislikePlaceholder;
  const showCounter = comment.length >= COMMENT_COUNTER_THRESHOLD;

  // Pill styles (Yes/No/Skip)
  const pillBase: React.CSSProperties = {
    appearance: "none",
    WebkitAppearance: "none",
    border: "1px solid var(--border)",
    borderRadius: 999,
    padding: "4px 12px",
    fontSize: 12,
    fontWeight: 500,
    background: "transparent",
    color: "var(--ink-2)",
    cursor: "pointer",
    transition: "background-color 120ms ease, color 120ms ease, border-color 120ms ease",
  };
  const pillActive: React.CSSProperties = {
    background: "var(--accent-soft)",
    color: "var(--accent)",
    borderColor: "var(--accent)",
  };

  return (
    <div
      role="region"
      aria-label={placeholder}
      style={{
        marginLeft: 36,
        marginTop: 8,
        padding: 12,
        background: "var(--card-2)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        animation: "midasV3FeedbackSlideIn 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Header row with close button */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: "var(--muted)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 500,
          }}
        >
          {action === "like" ? labels.likePlaceholder.replace(" (optional)", "").replace(" (voliteľné)", "") : labels.dislikePlaceholder.replace(" (optional)", "").replace(" (voliteľné)", "")}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label={labels.close}
          title={labels.close}
          style={{
            width: 22,
            height: 22,
            padding: 0,
            background: "transparent",
            border: 0,
            borderRadius: 4,
            cursor: "pointer",
            color: "var(--muted)",
            display: "inline-grid",
            placeItems: "center",
          }}
        >
          <X size={13} strokeWidth={2} />
        </button>
      </div>

      {/* Source-wrong toggle — Dislike only */}
      {action === "dislike" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 12,
              color: "var(--ink-2)",
              marginRight: 4,
            }}
          >
            {labels.sourceWrongQuestion}
          </span>
          <button
            type="button"
            onClick={() => setSourceWrong("yes")}
            aria-pressed={sourceWrong === "yes"}
            style={{ ...pillBase, ...(sourceWrong === "yes" ? pillActive : null) }}
          >
            {labels.yes}
          </button>
          <button
            type="button"
            onClick={() => setSourceWrong("no")}
            aria-pressed={sourceWrong === "no"}
            style={{ ...pillBase, ...(sourceWrong === "no" ? pillActive : null) }}
          >
            {labels.no}
          </button>
          <button
            type="button"
            onClick={() => setSourceWrong("skip")}
            aria-pressed={sourceWrong === "skip"}
            style={{ ...pillBase, ...(sourceWrong === "skip" ? pillActive : null) }}
          >
            {labels.skip}
          </button>
        </div>
      )}

      {/* Comment input + send button */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 8,
        }}
      >
        <textarea
          ref={textareaRef}
          value={comment}
          onChange={handleCommentChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={2}
          maxLength={MAX_COMMENT_LENGTH}
          style={{
            flex: 1,
            minHeight: 38,
            maxHeight: 120,
            resize: "vertical",
            padding: "8px 10px",
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            fontSize: 13,
            fontFamily: "inherit",
            color: "var(--ink)",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          aria-label={labels.submit}
          title={labels.submit}
          style={{
            width: 32,
            height: 38,
            padding: 0,
            background: submitting ? "var(--card-2)" : "var(--accent)",
            color: submitting ? "var(--muted)" : "white",
            border: 0,
            borderRadius: 6,
            cursor: submitting ? "not-allowed" : "pointer",
            display: "inline-grid",
            placeItems: "center",
            flexShrink: 0,
            transition: "background-color 120ms ease",
          }}
        >
          <Send size={14} strokeWidth={2} />
        </button>
      </div>

      {/* Footer row — char counter + status */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          minHeight: 14,
        }}
      >
        <span
          style={{
            fontSize: 10.5,
            color: "var(--faint)",
            fontFamily: "var(--font-mono, ui-monospace, monospace)",
            visibility: showCounter ? "visible" : "hidden",
          }}
        >
          {labels.charCount}
        </span>
        {recorded && (
          <span
            style={{
              fontSize: 11,
              color: "var(--accent)",
              fontWeight: 500,
              letterSpacing: "0.02em",
            }}
          >
            {labels.recorded}
          </span>
        )}
        {!recorded && feedback?.error && (
          <span
            style={{
              fontSize: 11,
              color: "var(--red, #C0392B)",
            }}
          >
            {labels.errorRetry}
          </span>
        )}
      </div>
    </div>
  );
};

export default FeedbackPanelV3;
